import { Button } from '@synoptic/ui-kit/button/button.js';
import { CheckIcon } from '@synoptic/ui-kit/icons/react/check.js';
import { DataTransferBothIcon } from '@synoptic/ui-kit/icons/react/data-transfer-both.js';
import { FireFlameIcon } from '@synoptic/ui-kit/icons/react/fire-flame.js';
import { LockIcon } from '@synoptic/ui-kit/icons/react/lock.js';
import { PackagesIcon } from '@synoptic/ui-kit/icons/react/packages.js';
import { ReceiveDollarsIcon } from '@synoptic/ui-kit/icons/react/receive-dollars.js';
import { ShareAndroidIcon } from '@synoptic/ui-kit/icons/react/share-android.js';
import clsx from 'clsx';
import { StripePriceFragmentFragment } from '@/__generated__/graphql';
import { Stream } from '@/__generated__/schema-types';
import { paths } from '@/routes-utils/paths.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars';
import { getMonthlyPrice } from '@/utils/paid-data';
import { isMatchingStream } from '../stream-matching';
import {
  followingPill,
  freeStreamPill,
  inPackageDisabledContainer,
  inPackagePill,
  matchingStreamPill,
  paidStreamContainer,
  paidStreamPill,
  streamDetailsContainer,
  streamListItem,
  streamListItemDescription,
  streamListItemDetails,
  streamListItemIconContainer,
  streamListItemMatchingIcon,
  streamListItemName,
} from './typeahead-multiselect.css';

interface StreamListItemProps {
  stream: Stream;
  inTier1?: boolean;
}

export const FreeStreamPill = () => {
  return (
    <div className={freeStreamPill}>
      <DataTransferBothIcon width={16} height={16} /> Free
    </div>
  );
};

export const InPackagePill = () => {
  return (
    <div className={inPackagePill}>
      <PackagesIcon width={16} height={16} /> In-Package
    </div>
  );
};

const MatchingPill = () => (
  <div className={matchingStreamPill}>
    <FireFlameIcon width={16} height={16} /> Matching
  </div>
);

export const IsFollowingPill = () => {
  return (
    <div className={followingPill}>
      <CheckIcon width={16} height={16} /> Following
    </div>
  );
};

const getStreamStatePill = (stream: Stream) => {
  if (stream.packageOnly) {
    return <InPackagePill />;
  }
  return stream.paidData?.length ? (
    <PaidStreamPill
      streamId={stream.id}
      paidData={stream.paidData}
      accessAllowed={stream.accessAllowed}
    />
  ) : (
    <FreeStreamPill />
  );
};

export const PaidStreamPill = ({
  streamId,
  paidData,
  accessAllowed,
}: {
  accessAllowed: boolean;
  streamId: string;
  paidData: StripePriceFragmentFragment[] | NonNullable<Stream['paidData']>;
}) => {
  const monthlyPrice = getMonthlyPrice(
    paidData as StripePriceFragmentFragment[],
  );
  return accessAllowed ? (
    <div className={paidStreamPill}>
      <ReceiveDollarsIcon width={16} height={16} /> Paid
    </div>
  ) : (
    <Button
      onClick={() => {
        window.open(paths.makeStream(streamId), '_blank');
      }}
      startIcon={<LockIcon width={16} height={16} />}
      size="small"
    >
      Unlock / ${monthlyPrice ? convertCentsToDollars(monthlyPrice) : 0}
    </Button>
  );
};

export const StreamListItem = ({
  stream,
  inTier1 = false,
}: StreamListItemProps) => {
  const isMatching = isMatchingStream(stream);
  const isSmartNews = isMatching || inTier1;

  return (
    <div
      className={clsx(streamListItem, {
        [paidStreamContainer]:
          !!stream.paidData?.length && !stream.canAddToStreamDeck,
        [inPackageDisabledContainer]:
          stream.packageOnly && !stream.canAddToStreamDeck,
      })}
    >
      <div
        className={clsx(
          streamListItemIconContainer,
          isSmartNews && streamListItemMatchingIcon,
        )}
      >
        {isSmartNews ? (
          <FireFlameIcon width={20} height={20} />
        ) : (
          <ShareAndroidIcon width={16} height={16} />
        )}
      </div>
      <div className={streamListItemDetails}>
        <span className={streamListItemName}>{stream.name}</span>
        <span className={streamListItemDescription}>{stream.description}</span>
      </div>
      <div className={streamDetailsContainer}>
        {stream.following && <IsFollowingPill />}
        {isMatching && <MatchingPill />}
        {getStreamStatePill(stream)}
      </div>
    </div>
  );
};
